import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import FileUpload from "@insite/mobius/FileUpload";
import React, { FC, RefObject, useState } from "react";
import {
    ErrorBadge,
    InstructionsBox,
    UploadLayout,
    UploadLeft,
    UploadRight,
} from "@dana/Components/DanaOrderUpload/DanaOrderUploadStyledComponents";
import { DanaOrderUploadFileUploadStyles } from "@dana/Components/DanaOrderUpload/DanaOrderUploadTypes";

import { css } from "styled-components";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Typography from "@insite/mobius/Typography";

interface Props {
    file: File | null;
    firstRowHeading: boolean;
    incorrectExtension: boolean;
    isBadFile: boolean;
    uploadLimitExceeded: boolean;
    fileUploadRef: RefObject<HTMLInputElement>;
    extendedStyles: DanaOrderUploadFileUploadStyles;
    onDownloadTemplate: () => void;
    onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onFirstRowHeadingChange: CheckboxProps["onChange"];
    onCancelUpload: () => void;
}

export const danaOrderUploadFileUploadStyles: DanaOrderUploadFileUploadStyles = {
    mainWrapper: {
        css: css`
            width: 100%;
            [class*="FormInputWrapper"] {
                .mobiusFileUpload {
                    &,
                    > input:not(.react-datetime-picker__inputGroup__input) {
                        &,
                        &:hover,
                        &:focus,
                        &:focus-within {
                            height: 42px;
                        }
                    }
                }
            }
            [class*="ButtonWrapper"] {
                min-height: 42px;
                line-height: initial;
            }
        `,
    },
    uploadLinkTooltipText: {
        css: css`
            margin-bottom: 15px;
        `,
    },
    downloadTemplateButton: {
        variant: "secondary",
        css: css`
            margin-bottom: 15px;
             ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                           width: 100%;
                        `
                    ],
                    "max",
                )}
        `,
    },
    fileUploader: {
        buttonProps: {
            variant: "secondary",
            css: css`
                margin-top: 26px;
            `
        },
    },
    includeFirstRowCheckbox: {
        css: css`
            margin: 15px 0;
            display: flex;
            align-items: center;
        `,
    },
    buttonWrapper: {
        css: css`
            display: flex;
            gap: 10px;
            margin-top: 16px;
        `
    },
    cancelUploadButton: {
        variant: "secondary",
        css: css`
            &:disabled {
                border: none;
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    css`
                        width: 100%;
                    `
                ],
                "max",
            )}
        `
    },
    uploadFileButton: {},
    spinner: {
        size: 20,
        css: css`
            margin-right: 6px;
        `,
    },
};

const DanaOrderUploadStep1File: FC<Props> = ({
    file,
    firstRowHeading,
    incorrectExtension,
    isBadFile,
    uploadLimitExceeded,
    fileUploadRef,
    extendedStyles,
    onDownloadTemplate,
    onFileChange,
    onFirstRowHeadingChange,
    onCancelUpload,
}) => {
    const [styles] = useState(() => mergeToNew(danaOrderUploadFileUploadStyles, extendedStyles));
    return (
        <StyledWrapper {...styles.mainWrapper}>
            <UploadLayout>
                <UploadLeft>
                    <Typography as="p" {...styles.uploadLinkTooltipText}>
                        {siteMessage("Dana_WishList_PartUpload_AddToListUploadFileMessageText")}
                    </Typography>
                    <Button 
                        {...styles.downloadTemplateButton} 
                        onClick={onDownloadTemplate}
                        data-test-selector="danaOrderUpload_downloadTemplate">
                        {translate("Download Template")}
                    </Button>
                    <FileUpload
                        {...styles.fileUploader}
                        inputRef={fileUploadRef as any}
                        fileName={file?.name ?? ""}
                        accept=".xls,.xlsx,.csv"
                        label={translate("Upload a File")}
                        labelPosition="top"
                        error={incorrectExtension ? translate("must be .xls, .xlsx or .csv") : undefined}
                        onChange={onFileChange}
                        uid="danaOrderUploadFile"
                    />
                    <Checkbox
                        {...styles.includeFirstRowCheckbox}
                        onChange={onFirstRowHeadingChange}
                        checked={firstRowHeading}
                        uid="danaIncludeHeading"
                        data-test-selector="danaOrderUpload_includeHeading"
                    >
                        {siteMessage("Dana_WishList_PartUpload_IncludeFirstRowHeading")}
                    </Checkbox>
                    {(isBadFile || uploadLimitExceeded) && (
                        <ErrorBadge>
                            {isBadFile
                                ? siteMessage("Dana_WishList_PartUpload_InvalidFileFormat")
                                : siteMessage("Dana_WishList_PartUpload_FileTooLarge")}
                        </ErrorBadge>
                    )}
                    {/* <StyledWrapper {...styles.buttonWrapper}>
                        <Button
                            {...styles.cancelUploadButton}
                            onClick={onCancelUpload}
                            disabled={!file}
                            data-test-selector="danaOrderUpload_cancelUpload"
                        >
                            {translate("Cancel Upload")}
                        </Button>
                    </StyledWrapper> */}
                </UploadLeft>
                <UploadRight>
                    <InstructionsBox>
                        {siteMessage("Dana_WishList_PartUpload_AddToListUploadFileModalInstructions")}
                    </InstructionsBox>
                </UploadRight>
            </UploadLayout>
        </StyledWrapper>
    )
};

export default DanaOrderUploadStep1File;
